import { useEffect, useState } from "react";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import axiosClient from "../../../api/axiosClient";
import SuperAdminBusinessSelector from "../../../components/layout/SuperAdminBusinessSelector";

const HISTORY = [[1, "1 hour"], [6, "6 hours"], [12, "12 hours"], [24, "24 hours"], [72, "3 days"], [168, "7 days"]];
const OVERDUE = [[5, "5 min"], [15, "15 min"], [30, "30 min"], [60, "1 hour"], [360, "6 hours"], [1440, "24 hours"]];
const HORIZON = [[60, "1 hour"], [360, "6 hours"], [720, "12 hours"], [1440, "24 hours"]];
const EXECUTIONS = ["Prepared", "Dispatched", "Skipped", "Failed", "Ambiguous"];
const WAITS = ["Awaiting", "Processing", "Completed", "Superseded", "Expired", "Cancelled"];
const JOBS = ["Pending", "Processing", "RetryScheduled", "Failed"];
const panel = "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm";

export function formatOverdueAge(minutes) {
  if (minutes == null) return "None";
  const whole = Math.max(0, Math.floor(minutes));
  if (whole < 1) return "<1 min";
  if (whole < 60) return `${whole} min`;
  if (whole < 1440) return `${Math.floor(whole / 60)}h ${whole % 60}m`;
  return `${Math.floor(whole / 1440)}d ${Math.floor((whole % 1440) / 60)}h`;
}

const count = (rows, status) => rows.find(row => row.status === status)?.count ?? 0;
const readable = value => value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");

function Metric({ label, value, attention = false }) {
  return (
    <div className={panel} role="group" aria-label={label}>
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className={`mt-2 text-2xl font-semibold tabular-nums ${attention && value > 0 ? "text-amber-700" : "text-slate-900"}`}>{value}</dd>
    </div>
  );
}

function StatusCounts({ rows, statuses }) {
  // Preserve unknown status identifiers without assigning them new semantics.
  const all = [...statuses, ...rows.map(row => row.status).filter(status => !statuses.includes(status))];
  return <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
    {all.map(status => <div key={status} role="group" aria-label={status} className="rounded-lg bg-slate-50 p-3">
      <dt className="text-sm text-slate-600 break-words">{status}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{count(rows, status)}</dd>
    </div>)}
  </dl>;
}

function Reasons({ title, rows }) {
  return <section className={panel} aria-label={title}>
    <h3 className="mb-3 font-semibold text-slate-900">{title}</h3>
    {rows.length === 0 ? <p className="text-sm text-slate-500">No terminal reasons in this window.</p> :
      <table className="w-full text-left text-sm">
        <thead className="text-slate-500"><tr><th className="pb-2 font-medium">Reason</th><th className="pb-2 text-right font-medium">Count</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.reason} className="border-t border-slate-100">
          <td className="py-2 pr-3 break-all text-slate-700">{readable(row.reason)}</td>
          <td className="py-2 text-right font-medium tabular-nums">{row.count}</td>
        </tr>)}</tbody>
      </table>}
  </section>;
}

function Filter({ label, value, options, onChange }) {
  return <label className="space-y-1 text-sm font-medium text-slate-700">
    <span className="block">{label}</span>
    <select className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3" value={value} onChange={event => onChange(Number(event.target.value))}>
      {options.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
    </select>
  </label>;
}

export default function AutomationMonitoringPage() {
  const [business, setBusiness] = useState(null);
  const [historicalWindowHours, setHistory] = useState(24);
  const [overdueMinutes, setOverdue] = useState(15);
  const [nearHorizonMinutes, setHorizon] = useState(60);
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const businessId = business?.id;

  useEffect(() => {
    setResult(null);
    setError(false);
    if (!businessId) { setLoading(false); return undefined; }
    let current = true;
    const controller = new AbortController();
    setLoading(true);
    axiosClient.get("/admin/follow-ups/diagnostics", {
      params: { businessId, historicalWindowHours, overdueMinutes, nearHorizonMinutes },
      // This page selects its own inspection scope; do not inherit another header context.
      __skipBizHeader: true,
      __silentGenericToast: true,
      __silent401: true,
      __silent403: true,
      signal: controller.signal,
    }).then(({ data }) => {
      if (current) setResult({ data, refreshedAt: data.generatedAtUtc || new Date().toISOString() });
    }).catch(() => {
      if (current) setError(true);
    }).finally(() => {
      if (current) setLoading(false);
    });
    return () => { current = false; controller.abort(); };
  }, [businessId, historicalWindowHours, overdueMinutes, nearHorizonMinutes, refresh]);

  const data = result?.data;
  return <div className="space-y-5 p-4 md:p-6">
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900"><Activity className="h-5 w-5 text-emerald-600" />Automation Monitoring</h1>
        <p className="mt-1 text-sm text-slate-600">Follow-up automation operational health for a selected business.</p>
      </div>
      <div className="flex flex-col items-end gap-2">
        <button type="button" onClick={() => setRefresh(value => value + 1)} disabled={!businessId || loading} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={16} />Refresh
        </button>
        {result && <p className="text-xs text-slate-500">Last refreshed <time dateTime={result.refreshedAt}>{new Date(result.refreshedAt).toLocaleString()}</time></p>}
      </div>
    </header>

    <section className={`${panel} grid gap-4 sm:grid-cols-2 xl:grid-cols-4`} aria-label="Monitoring filters">
      <div className="space-y-1">
        <p className="text-sm font-medium text-slate-700">Business</p>
        <SuperAdminBusinessSelector selectedId={businessId} selectedName={business?.name} onSelect={setBusiness} onClear={() => setBusiness(null)} showIdentifier align="left" />
        {businessId && <p className="break-all text-xs text-slate-500">{businessId}</p>}
      </div>
      <Filter label="Historical window" value={historicalWindowHours} options={HISTORY} onChange={setHistory} />
      <Filter label="Overdue threshold" value={overdueMinutes} options={OVERDUE} onChange={setOverdue} />
      <Filter label="Near-horizon threshold" value={nearHorizonMinutes} options={HORIZON} onChange={setHorizon} />
    </section>

    {!businessId ? <div className={`${panel} py-12 text-center text-sm text-slate-500`}>Select a business to view monitoring.</div> :
      loading ? <div role="status" className={`${panel} flex items-center justify-center gap-2 py-12 text-sm text-slate-500`}><Loader2 className="animate-spin text-emerald-600" size={20} />Loading monitoring...</div> :
      error ? <div role="alert" className={`${panel} flex flex-wrap items-center justify-between gap-3`}>
        <p className="text-sm text-slate-700">Monitoring could not be loaded. Please try again.</p>
        <button type="button" className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white" onClick={() => setRefresh(value => value + 1)}>Retry</button>
      </div> : data && <>
        <p className="text-xs text-slate-500">Active backlog is current. Failed and ambiguous jobs, step executions, terminal waits and terminal reasons use the selected historical window. Due and overdue counts overlap pending and retry jobs.</p>
        <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric label="Active Enrollments" value={data.activeEnrollments} />
          <Metric label="Pending Jobs" value={count(data.jobs.statuses, "Pending")} />
          <Metric label="Due Jobs" value={data.jobs.due} />
          <Metric label="Overdue Jobs" value={data.jobs.overdue} attention />
          <Metric label="Processing Jobs" value={count(data.jobs.statuses, "Processing")} />
          <Metric label="Retry Scheduled" value={count(data.jobs.statuses, "RetryScheduled")} />
          <Metric label="Failed Jobs" value={count(data.jobs.statuses, "Failed")} attention />
          <Metric label="Stale Processing Leases" value={data.jobs.staleProcessingLeases} attention />
          {data.jobs.statuses.filter(row => !JOBS.includes(row.status)).map(row => <Metric key={row.status} label={`${row.status} Jobs`} value={row.count} />)}
        </dl>
        <dl className={`${panel} flex items-center justify-between gap-3`}><div role="group" aria-label="Oldest overdue job" className="flex w-full items-center justify-between gap-3"><dt className="text-sm text-slate-600">Oldest overdue job</dt><dd className="text-lg font-semibold text-slate-900">{formatOverdueAge(data.jobs.oldestOverdueAgeMinutes)}</dd></div></dl>

        <section className={panel} aria-label="Step execution outcomes">
          <h2 className="font-semibold text-slate-900">Step execution outcomes</h2>
          <p className="mb-3 mt-1 text-xs text-slate-500">Recorded execution statuses in the selected historical window.</p>
          <StatusCounts rows={data.stepExecutions} statuses={EXECUTIONS} />
        </section>
        <section className={panel} aria-label="Durable waiting-response states">
          <h2 className="font-semibold text-slate-900">Durable waiting-response states</h2>
          <p className="mb-3 mt-1 text-xs text-slate-500">Operational AutoReply waits: current Awaiting and Processing states, plus terminal states in the selected window.</p>
          <StatusCounts rows={data.awaitingResponses.statuses} statuses={WAITS} />
          <dl className="mt-3 grid gap-3 sm:grid-cols-3">
            <Metric label="Stale Processing Claims" value={data.awaitingResponses.staleProcessingClaims} attention />
            <Metric label="Near horizon" value={data.awaitingResponses.nearHorizon} />
            <Metric label="Beyond horizon" value={data.awaitingResponses.beyondHorizon} attention />
          </dl>
        </section>
        <section aria-label="Terminal reasons">
          <h2 className="mb-1 font-semibold text-slate-900">Terminal reasons</h2>
          <p className="mb-3 text-xs text-slate-500">Top reasons returned for the selected window (up to 20 per group).</p>
          <div className="grid gap-3 lg:grid-cols-2">
            <Reasons title="Enrollment terminal reasons" rows={data.enrollmentTerminalReasons} />
            <Reasons title="Job failure / terminal reasons" rows={data.jobTerminalReasons} />
          </div>
        </section>
      </>}
  </div>;
}
